import { SelectAll } from '@mui/icons-material';
import { Grid, Tooltip, Chip, Divider, Paper } from '@mui/material';

import clsx from 'clsx';
import { ArrayHelpers, useFormikContext } from 'formik';
import { truncate } from 'lodash';

import { Course, Faculty } from 'types/AppTypes';

import '../styles/FacultyBox.scss';

interface Props {
  editMode?: boolean;
  faculty: Faculty;
  selectedCourses: Course[];
  arrayHelpers?: ArrayHelpers;
  dense?: boolean;
}

const FacultyBox = ({ faculty, selectedCourses, arrayHelpers, editMode, dense }: Props) => {
  const courseIDs = selectedCourses?.map(c => c.id);
  const intersections = faculty.courses.filter(c => courseIDs?.includes(c.id));
  const formik = useFormikContext();
  let active, indeterminate;
  if (intersections.length === faculty.courses.length) {
    active = true;
  } else if (intersections.length > 0) {
    indeterminate = true;
  }

  const getDenseTitle = (faculty: Faculty) => {
    return (
      <Tooltip placement="top" title={faculty.name}>
        <span>
          {faculty.courses.length <= 2
            ? faculty.shorthand
            : truncate(faculty.name, { length: faculty.courses.length * 7 })}
        </span>
      </Tooltip>
    );
  };

  const readOnlyFields = () => (
    <Grid component={Paper} item xs={dense ? 'auto' : 12} lg="auto" className="faculty-list__box">
      <Grid container item className="faculty-list__box__header">
        <span>{dense ? getDenseTitle(faculty) : faculty.name}</span>
      </Grid>
      <Divider />
      <Grid container item className="faculty-list__box__courses">
        {faculty.courses?.map(c => (
          <Tooltip key={c.id} placement="bottom" title={c.name}>
            <Chip
              component={Paper}
              label={c.shorthand}
              className={clsx('faculty-list__box__courses__item', {
                selected: courseIDs?.includes(c.id),
                'faculty-list__box__courses__item--readonly': !editMode
              })}
            />
          </Tooltip>
        ))}
      </Grid>
    </Grid>
  );

  if (!editMode) {
    return readOnlyFields();
  }

  const { setFieldValue } = formik;

  const handleSelectAllClick = () => {
    if (editMode) {
      if (intersections.length === 0) {
        faculty.courses.forEach(c => {
          arrayHelpers?.push(c);
        });
      } else if (intersections.length === faculty.courses.length) {
        const courseIDs = faculty.courses?.map(c => c.id);
        setFieldValue(
          'courses',
          selectedCourses.filter(sc => !courseIDs?.includes(sc.id))
        );
      } else {
        faculty.courses.forEach(c => {
          const idx = selectedCourses.findIndex(sc => sc.id === c.id);
          if (idx === -1) {
            arrayHelpers?.push(c);
          }
        });
      }
    }
  };

  const handleSelectClick = (c: Course) => {
    if (editMode) {
      const idx = selectedCourses.findIndex(sc => sc.id === c.id);
      if (idx === -1) {
        arrayHelpers?.push(c);
      } else {
        arrayHelpers?.remove(idx);
      }
    }
  };

  return (
    <Grid
      component={Paper}
      item
      xs={dense ? 'auto' : 12}
      lg="auto"
      className={clsx('faculty-list__box', {
        'faculty-list__box--dense': dense
      })}
    >
      <Grid container item className="faculty-list__box__header">
        {dense ? getDenseTitle(faculty) : faculty.name}
        {editMode && (
          <Tooltip placement="right" title="Select All">
            <SelectAll
              className={clsx('faculty-list__box__header__select-all', {
                active,
                indeterminate
              })}
              onClick={handleSelectAllClick}
            />
          </Tooltip>
        )}
      </Grid>
      <Divider />
      <Grid container item className="faculty-list__box__courses">
        {faculty.courses?.map(c => (
          <Tooltip key={c.id} placement="bottom" title={c.name}>
            <Chip
              onClick={() => handleSelectClick(c)}
              component={Paper}
              label={c.shorthand}
              className={clsx('faculty-list__box__courses__item', {
                selected: courseIDs?.includes(c.id),
                'faculty-list__box__courses__item--readonly': !editMode
              })}
            />
          </Tooltip>
        ))}
      </Grid>
    </Grid>
  );
};

export default FacultyBox;
